import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Snackbar
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import AdUnitsIcon from '@mui/icons-material/AdUnits';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import { apiService, Ad, Client } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ClientCreateAdModal from './ClientCreateAdModal';

const AdsList: React.FC = () => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createAdModalOpen, setCreateAdModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adToDelete, setAdToDelete] = useState<Ad | null>(null);
  const [adToEdit, setAdToEdit] = useState<Ad | null>(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const navigate = useNavigate();
  const { clientSecret, logout } = useAuth();

  useEffect(() => {
    if (!clientSecret) {
      navigate('/login');
      return;
    }

    fetchAds();
    fetchClientData();
  }, [clientSecret, navigate]);

  const handleAdClick = (ad: Ad) => {
    navigate(`/ad/${encodeURIComponent(ad.tx_ad_name)}`, { state: { ad } });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleOperatorsSpaceClick = () => {
    if (client?.tx_operators_space_link) {
      window.open(client.tx_operators_space_link, '_blank');
    }
  };

  const handleAdCreated = () => {
    fetchAds();
  };

  const fetchAds = async () => {
    try {
      const adsData = await apiService.getAds(clientSecret!);
      setAds(adsData);
    } catch (err) {
      setError('Failed to fetch ads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientData = async () => {
    try {
      const clientData = await apiService.getClientBySecret(clientSecret!);
      setClient(clientData);
    } catch (err) {
      console.error('Failed to fetch client data:', err);
    }
  };

  const handleDeleteClick = (ad: Ad, event: React.MouseEvent) => {
    event.stopPropagation();
    setAdToDelete(ad);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!adToDelete || !clientSecret) return;
    
    try {
      await apiService.deleteAd(adToDelete.tx_ad_name, clientSecret, adToDelete.tx_match_id || undefined);
      setDeleteDialogOpen(false);
      setAdToDelete(null);
      fetchAds(); // Refresh the list
    } catch (error) {
      setError('Failed to delete ad. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAdToDelete(null);
  };

  const handleEditClick = (ad: Ad, event: React.MouseEvent) => {
    event.stopPropagation();
    setAdToEdit(ad);
    setCreateAdModalOpen(true);
  };

  const handleViewClick = (ad: Ad, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/ad/${encodeURIComponent(ad.tx_ad_name)}`, { state: { ad } });
  };

  const handleCopyLink = async (ad: Ad, event: React.MouseEvent) => {
    event.stopPropagation();
    
    try {
      // Build the URL with the required format
      let url = 'https://iframe.extratime.cloud/extrastreamer-cloud/';

      // Add bundle name and version if available
      if (ad.bundle_ad?.bundle) {
        // Always use tx_name/tx_version for the URL
        url += `${ad.bundle_ad.bundle.tx_name}/${ad.bundle_ad.bundle.tx_version}`;
      } else {
        url += 'default/v1';
      }
      
      // Add query parameters
      const params = new URLSearchParams();
      
      // Add match_id
      if (ad.tx_match_id) {
        params.append('matchId', ad.tx_match_id);
      }
      
      // Add ad name
      params.append('adName', ad.tx_ad_name);
      
      // Add client secret
      if (clientSecret) {
        params.append('secret', clientSecret);
      }
      
      // Parse and add bundle fields from tx_ad_data
      if (ad.client_field_values) {
        try {
          console.log(ad.client_field_values)
          const adData = ad.client_field_values;
          console.log('Parsed ad data:', adData); // Debug log
          
          // Handle new format: {"bundle_fields": {...}}
          if (adData.bundle_fields) {
            Object.entries(adData.bundle_fields).forEach(([key, value]) => {
              params.append(key, String(value));
              console.log(`Added param: ${key}=${value}`); // Debug log
            });
          } 
          // Handle old format: direct bundle fields object
          else if (typeof adData === 'object' && !Array.isArray(adData)) {
            Object.entries(adData).forEach(([key, value]) => {
              params.append(key, String(value));
              console.log(`Added param (legacy): ${key}=${value}`); // Debug log
            });
          }
        } catch (error) {
          console.error('Failed to parse ad data:', error);
        }
      }
      
      // Build final URL
      const finalUrl = url + '?' + params.toString();
      console.log('Generated URL:', finalUrl); // Debug log
      
      // Copy to clipboard
      await navigator.clipboard.writeText(finalUrl);
      setCopySnackbarOpen(true);
      
    } catch (error) {
      console.error('Failed to copy link:', error);
      setError('Failed to copy link to clipboard');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <AdUnitsIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Ads Manager
          </Typography>
          {client?.tx_operators_space_link && (
            <Tooltip title="Operators space">
              <IconButton color="inherit" onClick={handleOperatorsSpaceClick}>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3}>
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Your Ads
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Click on an ad to view its details
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateAdModalOpen(true)}
            >
              Create New Ad
            </Button>
          </Box>
          <Divider />
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}
          {ads.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" color="text.secondary">
                No ads found for this client.
              </Typography>
            </Box>
          ) : (
            <List>
              {ads.map((ad, index) => (
                <React.Fragment key={ad.id}>
                  {index > 0 && <Divider />}
                  <ListItem 
                    disablePadding
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Copy ad link to clipboard">
                          <IconButton
                            edge="end"
                            aria-label="copy link"
                            onClick={(e) => handleCopyLink(ad, e)}
                            color="primary"
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          edge="end"
                          aria-label="view"
                          onClick={(e) => handleViewClick(ad, e)}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="edit"
                          onClick={(e) => handleEditClick(ad, e)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={(e) => handleDeleteClick(ad, e)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemButton onClick={() => handleAdClick(ad)} sx={{ pr: 20 }}>
                      <ListItemText
                        primary={ad.tx_ad_name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              ID: {ad.id}
                              {ad.tx_match_id && (
                                <>
                                  {' • '}
                                  Match ID: {ad.tx_match_id}
                                </>
                              )}
                            </Typography>
                            {ad.client_field_values && Object.keys(ad.client_field_values).length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {Object.entries(ad.client_field_values).map(([key, value]) => {
                                    // Try to find the client-facing field name from bundle fields
                                    const bundleField = ad.bundle_ad?.bundle?.bundle_fields?.find(field => field.tx_name === key);
                                    const displayName = bundleField?.tx_client_field_name || key;

                                    return (
                                      <Box
                                        key={key}
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          bgcolor: 'primary.light',
                                          color: 'primary.contrastText',
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: 1,
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        <strong>{displayName}:</strong>&nbsp;{String(value)}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{
                          fontWeight: 'medium',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Container>

      <ClientCreateAdModal
        open={createAdModalOpen}
        onClose={() => {
          setCreateAdModalOpen(false);
          setAdToEdit(null);
        }}
        clientSecret={clientSecret!}
        onAdCreated={handleAdCreated}
        editAd={adToEdit}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Ad
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the ad "{adToDelete?.tx_ad_name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Ad link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default AdsList;